import type { BundlePickerTier, Category, Product } from '#bv/data/products'
import {
  SERVER_BOOST_3M_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_ID,
  SERVER_BOOST_PRODUCT_IDS,
} from '#bv/data/serverBoostIds'
import { SPOTIFY_1M_SHARED_PRODUCT_ID } from '#bv/data/storefrontProductIds'
import { tierListingBlurb } from '#bv/lib/bundleTierBlurb'
import { productImagePath } from '#bv/lib/productImageSlug'

/** Synthetic storefront ids for bundle hub cards (base catalog ends ~129; SMM uses 131–133). */
export const PRODUCT_FAMILY_HUB_IDS = {
  netflix: 200,
  disneyPlus: 202,
  youtubePremium: 203,
  crunchyroll: 204,
  primeVideo: 205,
  paramount: 206,
  spotify: 207,
  epicNfa: 208,
  epicFa: 209,
  fortnite: 210,
  steamLifetime: 211,
  vpnLifetime: 212,
  vBucks: 213,
  discord: 214,
  creativeTools: 216,
  sportsStreaming: 217,
  intlStreaming: 218,
} as const

/** Suggested homepage featured slots when admin leaves featured empty (hub ids). */
export const DEFAULT_FEATURED_HUB_IDS: number[] = [
  PRODUCT_FAMILY_HUB_IDS.netflix,
  PRODUCT_FAMILY_HUB_IDS.discord,
  PRODUCT_FAMILY_HUB_IDS.spotify,
  PRODUCT_FAMILY_HUB_IDS.vpnLifetime,
]

export type ProductFamilyContext = {
  hubId: number
  familyName: string
  pickerTitle: string
  tiers: BundlePickerTier[]
}

type FamilyDef = {
  hubId: number
  name: string
  image: string
  category: Category
  pickerTitle: string
  pickerDescription: string
  childIds: number[]
  tierLabel: (child: Product) => string
  badge?: Product['badge']
}

function tierLabelAfterPrefix(prefix: RegExp, child: Product): string {
  const stripped = child.name.replace(prefix, '').trim()
  return stripped || child.name
}

const FAMILIES: FamilyDef[] = [
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.netflix,
    name: 'Netflix',
    image: productImagePath('Netflix Shared Access'),
    category: 'streaming',
    pickerTitle: 'Choose your Netflix tier',
    pickerDescription: 'Shared access, FA 4K, or key-based shared access with unlimited replacements — pick what fits you.',
    childIds: [16, 17, 18, 19, 20],
    tierLabel: (c) => tierLabelAfterPrefix(/^netflix\s*/i, c),
    badge: 'bestseller',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.disneyPlus,
    name: 'Disney+',
    image: productImagePath('Disney+ Shared Access'),
    category: 'streaming',
    pickerTitle: 'Choose your Disney+ bundle',
    pickerDescription: 'Standalone Disney+ or bundled with Hulu and ESPN+.',
    childIds: [5, 6, 7],
    tierLabel: (c) => tierLabelAfterPrefix(/^disney\+\s*/i, c),
    badge: 'popular',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.youtubePremium,
    name: 'YouTube Premium',
    image: productImagePath('Youtube Premium Shared Access FA'),
    category: 'streaming',
    pickerTitle: 'Choose your YouTube Premium plan',
    pickerDescription: 'Monthly FA, family owner, or shared-account Premium access.',
    childIds: [2, 3, 4],
    tierLabel: (c) => tierLabelAfterPrefix(/^youtube premium\s*/i, c),
    badge: 'popular',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.crunchyroll,
    name: 'Crunchyroll',
    image: productImagePath('Crunchyroll Shared Access (Fan)'),
    category: 'streaming',
    pickerTitle: 'Choose your Crunchyroll tier',
    pickerDescription: 'Fan or Mega Fan shared access.',
    childIds: [8, 9],
    tierLabel: (c) => tierLabelAfterPrefix(/^crunchyroll\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.primeVideo,
    name: 'Prime Video',
    image: productImagePath('Prime Video Shared Access'),
    category: 'streaming',
    pickerTitle: 'Choose your Prime Video plan',
    pickerDescription: 'One month, six months FA, or shared access.',
    childIds: [22, 23, 129],
    tierLabel: (c) => tierLabelAfterPrefix(/^prime video\s*/i, c),
    badge: 'popular',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.paramount,
    name: 'Paramount+',
    image: productImagePath('Paramount+ Shared Access (EU)'),
    category: 'streaming',
    pickerTitle: 'Choose your Paramount+ region',
    pickerDescription: 'EU or USA shared access listings.',
    childIds: [28, 29],
    tierLabel: (c) => tierLabelAfterPrefix(/^paramount\+\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.spotify,
    name: 'Spotify',
    image: productImagePath('Spotify Shared Access'),
    category: 'streaming',
    pickerTitle: 'Choose your Spotify shared account',
    pickerDescription: 'Shared-account Premium access — 1 month, 3 months, or extended slot.',
    childIds: [81, 82, SPOTIFY_1M_SHARED_PRODUCT_ID],
    tierLabel: (c) => {
      const n = c.name
      if (/\(1\s*month\)/i.test(n)) return '1 Month — Shared Account'
      if (/\(3\s*months?\)/i.test(n)) return '3 Months — Shared Account'
      if (/extended/i.test(n)) return 'Extended — Shared Account'
      return tierLabelAfterPrefix(/^spotify\s*/i, c)
    },
    badge: 'bestseller',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.epicNfa,
    name: 'Epic Games (NFA)',
    image: productImagePath('EpicGames NFA (350+ Games)'),
    category: 'gaming',
    pickerTitle: 'Choose your NFA library size',
    pickerDescription: 'Not-full-access accounts sorted by game count.',
    childIds: [36, 37, 38, 39],
    tierLabel: (c) => tierLabelAfterPrefix(/^epic\s*games?\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.epicFa,
    name: 'Epic Games (FA)',
    image: productImagePath('Epic Games FA (350+ Games)'),
    category: 'gaming',
    pickerTitle: 'Choose your FA library size',
    pickerDescription: 'Full-access accounts with more control — pick by game count.',
    childIds: [40, 41, 42],
    tierLabel: (c) => tierLabelAfterPrefix(/^epic\s*games?\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.fortnite,
    name: 'Fortnite Accounts',
    image: productImagePath('Fortnite 50-100 Skins'),
    category: 'gaming',
    pickerTitle: 'Choose your Fortnite account',
    pickerDescription: 'Skin count, rare cosmetics, and price tiers — each listing is a different account band.',
    childIds: [59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74],
    tierLabel: (c) => tierLabelAfterPrefix(/^fortnite\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.steamLifetime,
    name: 'Steam Shared Access Games',
    image: productImagePath('Steam Shared Access: GTA'),
    category: 'gaming',
    pickerTitle: 'Choose your Steam game',
    pickerDescription: 'Shared-account Steam access per title — pick the game you want.',
    childIds: [
      84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106,
      107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 124, 125, 126,
    ],
    tierLabel: (c) => tierLabelAfterPrefix(/^steam shared access:\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.vpnLifetime,
    name: 'VPN Shared Access',
    image: productImagePath('Nord VPN Shared Access'),
    category: 'software',
    pickerTitle: 'Choose your VPN',
    pickerDescription: 'Shared account access by provider — Express, Nord, IPVanish, TunnelBear, and more.',
    childIds: [24, 25, 33, 52],
    tierLabel: (c) => c.name.replace(/\s*shared access$/i, '').trim(),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.vBucks,
    name: 'Fortnite V-Bucks',
    image: productImagePath('V-Bucks 1000-2500'),
    category: 'gaming',
    pickerTitle: 'Choose your V-Bucks amount',
    pickerDescription: 'Different balance bands — pick the package that matches what you need.',
    childIds: [75, 76],
    tierLabel: (c) => tierLabelAfterPrefix(/^v-bucks\s*/i, c),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.discord,
    name: 'Discord',
    image: productImagePath('Discord Server Boosts'),
    category: 'discord',
    pickerTitle: 'Choose your Discord product',
    pickerDescription: 'Nitro promos, server boosts, verified tokens, and decorations.',
    childIds: [35, 78, 79, 80, 134, 135],
    tierLabel: (c) => {
      const n = c.name
      if (c.id === SERVER_BOOST_PRODUCT_ID) return 'Server Boosts — 1 Month (packages)'
      if (c.id === SERVER_BOOST_3M_PRODUCT_ID) return 'Server Boosts — 3 Months (packages)'
      if (/nitro promo/i.test(n)) {
        if (/\(3\s*months?\)/i.test(n)) return 'Nitro — 3 Months'
        if (/\(1\s*month\)/i.test(n)) return 'Nitro — 1 Month'
        return 'Nitro Promo'
      }
      if (/verified tokens/i.test(n)) return 'Verified Tokens'
      if (/decoration/i.test(n)) return 'Random Decoration'
      return n
    },
    badge: 'bestseller',
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.creativeTools,
    name: 'Creative & AI Tools',
    image: productImagePath('Capcut Shared Access'),
    category: 'software',
    pickerTitle: 'Choose your tool',
    pickerDescription: 'CapCut, Canva, Wondershare Filmora, and ChatGPT shared access listings.',
    childIds: [1, 21, 31, 127, 128],
    tierLabel: (c) => c.name.replace(/\s*shared access$/i, '').trim(),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.sportsStreaming,
    name: 'Sports & Live TV',
    image: productImagePath('HboMax Shared Access'),
    category: 'streaming',
    pickerTitle: 'Choose your sports / live TV pass',
    pickerDescription: 'UFC, NBA, HBO Max, and similar shared-account streaming access.',
    childIds: [26, 27, 32],
    tierLabel: (c) => c.name.replace(/\s*shared access$/i, '').trim(),
  },
  {
    hubId: PRODUCT_FAMILY_HUB_IDS.intlStreaming,
    name: 'International Streaming',
    image: productImagePath('TodTV'),
    category: 'streaming',
    pickerTitle: 'Choose your service',
    pickerDescription: 'Regional and niche streaming shared access.',
    childIds: [30, 44, 49, 50, 51],
    tierLabel: (c) => c.name.replace(/\s*shared access$/i, '').trim(),
  },
]

const HUB_CHILD_IDS = new Map<number, number[]>()
for (const family of FAMILIES) {
  HUB_CHILD_IDS.set(family.hubId, [...family.childIds])
}

const HUB_IDS = new Set(FAMILIES.map((f) => f.hubId))

const CHILD_TO_FAMILY = new Map<number, FamilyDef>()
for (const family of FAMILIES) {
  for (const childId of family.childIds) {
    CHILD_TO_FAMILY.set(childId, family)
  }
}

export function isProductFamilyHubId(productId: number): boolean {
  return HUB_IDS.has(productId)
}

export function isProductFamilyChildId(productId: number): boolean {
  return CHILD_TO_FAMILY.has(productId)
}

export function getHubChildProductIds(hubId: number): number[] | null {
  return HUB_CHILD_IDS.get(hubId) ?? null
}

export function getCanonicalProductId(productId: number): number {
  return CHILD_TO_FAMILY.get(productId)?.hubId ?? productId
}

export function isProductFamilyTierPage(productId: number): boolean {
  return CHILD_TO_FAMILY.has(productId)
}

/** Map a tier SKU id (or hub id) to the shop hub card id for featured picks / admin. */
export function featuredProductIdForShop(productId: number): number {
  return getCanonicalProductId(productId)
}

export function cartLineDisplayName(productId: number, productName: string, catalog: Product[]): string {
  const ctx = getProductFamilyContext(productId, catalog)
  const tier = getProductFamilyTierLabel(productId, catalog)
  if (ctx && tier) return `${ctx.familyName} — ${tier}`
  return productName
}

/** Collapse legacy featured tier ids to hub ids; dedupe while preserving order. */
export function normalizeFeaturedProductIds(ids: number[]): number[] {
  const seen = new Set<number>()
  const out: number[] = []
  for (const id of ids) {
    const mapped = featuredProductIdForShop(id)
    if (mapped > 0 && !seen.has(mapped)) {
      seen.add(mapped)
      out.push(mapped)
    }
  }
  return out
}

export function getProductFamilyTierLabel(productId: number, catalog: Product[]): string | null {
  const ctx = getProductFamilyContext(productId, catalog)
  if (!ctx) return null
  return ctx.tiers.find((t) => t.productId === productId)?.label ?? null
}

/** Tier siblings for a catalog SKU that belongs to a product family (not hub cards). */
export function getProductFamilyContext(
  productId: number,
  catalog: Product[],
): ProductFamilyContext | null {
  const family = CHILD_TO_FAMILY.get(productId)
  if (!family) return null
  const hub = catalog.find((p) => p.id === family.hubId)
  const tiers = hub?.bundlePicker?.tiers
  if (!tiers?.length) return null
  return {
    hubId: family.hubId,
    familyName: family.name,
    pickerTitle: family.pickerTitle,
    tiers,
  }
}

function buildTiers(family: FamilyDef, children: Product[]): BundlePickerTier[] {
  return [...children]
    .sort((a, b) => a.price - b.price || a.name.localeCompare(b.name))
    .map((child) => ({
      label: family.tierLabel(child),
      productId: child.id,
      price: child.price,
      blurb: tierListingBlurb(child) ?? undefined,
    }))
}

function buildHub(family: FamilyDef, children: Product[]): Product {
  const minPrice = Math.min(...children.map((c) => c.price))
  const tierCount = children.length
  const childBadge = children.find((c) => c.badge)?.badge
  const tierNames = children.map((c) => family.tierLabel(c))
  const searchHint =
    tierNames.length <= 6
      ? tierNames.join(', ')
      : `${tierNames.slice(0, 5).join(', ')}, and ${tierNames.length - 5} more`

  return {
    id: family.hubId,
    name: family.name,
    image: family.image,
    category: family.category,
    description: [
      `${family.name} — ${tierCount} options.`,
      family.pickerDescription,
      `Search: ${searchHint}.`,
    ].join(' '),
    shortDescription: family.pickerDescription,
    price: minPrice,
    ...(family.badge ?? childBadge ? { badge: family.badge ?? childBadge } : {}),
    stock: 999,
    rating: Math.max(...children.map((c) => c.rating)),
    reviewCount: children.reduce((sum, c) => sum + c.reviewCount, 0),
    deliveryInfo: 'Select a tier below — each option has its own delivery and support details.',
    features: [
      `${tierCount} tiers to compare`,
      'Same checkout and support as individual listings',
      'Full product page per tier before you buy',
    ],
    bundlePicker: {
      title: family.pickerTitle,
      description: family.pickerDescription,
      tiers: buildTiers(family, children),
    },
  }
}

/**
 * Collapse multi-SKU families into shop hub cards; tier SKUs stay in catalog for PDP/checkout.
 */
export function applyProductFamilies(catalog: Product[]): Product[] {
  const byId = new Map(catalog.map((p) => [p.id, p]))
  const hiddenIds = new Set<number>()
  const hubs: Product[] = []
  const hubAtAnchor = new Map<number, Product>()

  for (const family of FAMILIES) {
    const children = family.childIds.map((id) => byId.get(id)).filter((p): p is Product => Boolean(p))
    if (children.length < 2) continue
    for (const child of children) {
      // Custom server boost SKU stays visible in shop (like Robux #130).
      if (!(SERVER_BOOST_PRODUCT_IDS as readonly number[]).includes(child.id)) hiddenIds.add(child.id)
    }
    const hub = buildHub(family, children)
    hubs.push(hub)
    hubAtAnchor.set(family.childIds[0], hub)
  }

  const updated = catalog.map((p) => (hiddenIds.has(p.id) ? { ...p, hideFromShop: true } : p))

  const result: Product[] = []
  for (const p of updated) {
    const hub = hubAtAnchor.get(p.id)
    if (hub) result.push(hub)
    result.push(p)
  }

  return result
}
